import type { Predicate, SearchDescriptor } from '@shared/core/search';
import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { AgMultiAutocomplete } from 'src/components/ui/AgMultiAutocomplete';

import { searchStateText } from '../cells/searchStateText';
import { useSearch } from '../hooks/useSearch';

export interface EntityMultiPickerProps<TDoc, T extends object> {
  readonly descriptor: SearchDescriptor<TDoc>;
  readonly filter?: Predicate<TDoc>;
  readonly value: readonly T[];
  readonly onChange: (value: T[]) => void;
  readonly toOption: (doc: TDoc) => T | null;
  readonly getOptionLabel: (option: T) => string;
  readonly getOptionKey: (option: T) => string | number;
  readonly label?: string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly emptyText?: ReactNode;
  readonly className?: string;
}

export function EntityMultiPicker<TDoc, T extends object>({
  descriptor,
  filter,
  value,
  onChange,
  toOption,
  getOptionLabel,
  getOptionKey,
  label,
  placeholder,
  disabled,
  emptyText = 'No matches',
  className,
}: EntityMultiPickerProps<TDoc, T>) {
  const [query, setQuery] = useState('');
  const { hits, state } = useSearch({ descriptor, query, filter });

  const options = useMemo(
    () => (state === 'forbidden' ? [] : hits.flatMap((doc) => toOption(doc) ?? [])),
    [hits, state, toOption]
  );

  const handleChange = useCallback(
    (next: T[]) => {
      onChange(next);
      setQuery('');
    },
    [onChange]
  );

  return (
    <AgMultiAutocomplete<T>
      label={label}
      placeholder={placeholder}
      className={className}
      isDisabled={disabled}
      value={[...value]}
      options={options}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      getOptionKey={getOptionKey}
      filterOptions={false}
      onInputChange={setQuery}
      isLoading={state === 'loading'}
      noOptionsText={searchStateText(state, emptyText)}
    />
  );
}
